/** The AutoCAD shell as the studio sees it — one hook, returning slots.
 *
 * ⚠ Everything AutoCAD-specific lives in this folder: the transcription (`catalog.ts`), the
 * icons, the chrome (`AutoCadShell.tsx`) and the wiring (`handlers.ts`). The studio gets one
 * `chrome.top` / `rail` / `side` / `status` / `overlay`. That slot contract came from the
 * design app at `/opt/dsn`, which really does host three interfaces over one canvas; **pdcad has
 * exactly one** and pdray / pdskp are SEPARATE PRODUCTS, not modes of this one.
 *
 * ⚠⚠ **THIS HOOK RUNS EVEN WHEN AUTOCAD MODE IS OFF.** React counts hooks by position, so
 * calling only the chosen shell's hook would throw the moment somebody switched interface. It
 * is cheap — some state and two memos — and `ctx.active` gates the only expensive thing, which
 * is rendering. Do not add an early return above a hook here.
 *
 * ⚠⚠ **EVERY `useState` THIS FILE'S HANDLER MEMO READS IS DECLARED ABOVE IT.** `useMemo` invokes
 * its callback during the same render, so a `const` declared below it is in the temporal dead
 * zone — which throws "Cannot access X before initialization" and renders a BLANK PAGE. tsc
 * cannot see it, because the read is inside a callback. Another lane shipped that bug and
 * only opening the page found it.
 */
import * as React from "react";
import AcadTestDrive from "./AcadTestDrive";
import { Suspense, lazy, useEffect, useMemo, useRef, useState } from "react";

/** ⚠⚠ LAZY, AND IT MUST STAY LAZY. A static import here puts three.js on the studio page for
 *  every drafter who never opens a 3D view — **219 KB, 36% of the page's javascript**, measured.
 *  What is deferred is the MODULE, not the hook; `useAutoCadStudioChrome` still runs every
 *  render whether or not anyone is in a 3D view. */
const Model3D = lazy(() => import("../three/Model3D"));
/** ⚠ Lazy for the same reason: paper space is a place most sessions never go, and the composer
 *  pulls in its own drag machinery. */
const SheetComposer = lazy(() => import("../sheets/SheetComposer"));

import type { ShellContext, StudioChrome, StudioShell } from "./shell-types";
import {
  AcadAppMenu, AcadCanvasFurniture, AcadCommandLine, AcadFileTabs, AcadLayoutTabs, AcadMenuBar,
  AcadAttrDialog, AcadBodyDialog, AcadFind, AcadImport, AcadParamsDialog, AcadPdfImport,
  AcadLayerTool, AcadViewportPane,
  AcadOptions, AcadPalettes, AcadPlot, AcadQSelect, AcadRibbon, AcadStatusBar, AcadThemeBridge,
  AcadTitleBar,
  AcadToolbars, AcadUcsIcon, AcadViewManager,
  DEFAULT_PLOT, osnapMenu, useAutoCadChrome, type PlotCfg,
} from "./AutoCadShell";
import { designExport, designImportCad, designInterfere, designLayersApi,
         designAddBody, designAddLight, designAddSurface, designBlockAttrs,
         designBlockEdit, designBlockEditOp, designDelLight, designDelSurface,
         designLights, designMakeBlock, designMeshSolid, designParametric,
         designSurfaces,
         designParametricDel, designParametricPost, designPdfImport,
         designPdfProbe, designPlansApi, designSetBlockAttrs, designSheets,
         designSolidOpAdd }
  from "./api";
import { BTN_BY_ID, PALETTES, WORKSPACES, type AcadMenuItem } from "./catalog";
import { POLAR_ANGLES, acadOfMode, buildAcadHandlers } from "./handlers";

/** Which palettes are docked, remembered.
 *
 * ⚠ Merged over the defaults, never used raw — a blob saved before a palette existed would
 * otherwise leave that one permanently `undefined`, i.e. silently unopenable forever. */
const PAL_KEY = "pderp.design.acad.palettes";
const WS_KEY = "pderp.design.acad.workspace";
const VP_KEY = "pderp.design.acad.vports";
/** The Plot dialog's settings, remembered the way AutoCAD remembers a page setup. */
const PLOT_KEY = "pderp.design.acad.plot";
const PAL_DEFAULT: Record<string, boolean> =
  Object.fromEntries(PALETTES.map((p) => [p.id, !!p.open]));

/** Which layer tool wants the dialog, and what it does with the answer.
 *
 * ⚠ ONE dialog for five commands rather than five dialogs. LAYMCH, COPYTOLAYER, LAYMRG, LAYDEL
 * and LAYWALK all ask the same question — "which layer?" — and differ only in the verb and in
 * whether they need a second answer. Five near-identical modals is five places for the layer
 * list to be built differently.
 */
export interface LayerDlg {
  mode: "pick" | "merge" | "delete" | "walk" | "states";
  /** The dialog's title bar — the AutoCAD command's own name. */
  title: string;
  /** What the OK button says, so the destructive ones read as destructive. */
  verb?: string;
  /** A line under the title saying what will happen, in numbers where there are numbers. */
  hint?: string;
  onPick?: (a: string, b?: string) => void;
}

/** AutoCAD prints the running snap it has caught as `_endp`, `_mid`, `_int`. */
const SNAP_TAG: Record<string, string> = {
  endpoint: "_endp", midpoint: "_mid", intersection: "_int", perpendicular: "_per",
  centre: "_cen", nearest: "_nea", node: "_nod", grid: "_grid", axis: "_pol",
  // ⚠ AutoCAD prints no tag for a tracking hit — it draws the ray instead. `_track` is ours,
  // because the status bar is the only place that says WHY the cursor stopped where it did.
  track: "_track",
  // ⚠ AutoCAD's own abbreviations for the six snaps built on 2026-08-31, so the status bar
  // reads the way a drafter's does. `_gcen`, `_appint` and `_par` are the exact strings
  // AutoCAD prints; getting them nearly right would be worse than leaving them off, because
  // the whole value of the readout is that it is recognised without being read.
  geocentre: "_gcen", quadrant: "_qua", insertion: "_ins", extension: "_ext",
  tangent: "_tan", parallel: "_par", apparent: "_appint",
};

export function useAutoCadStudioChrome(ctx: ShellContext): StudioChrome {
  const { A, T, st, mode } = ctx;

  // ⚠⚠ ABOVE THE MEMO THAT READS IT — see the header. `handlers.ts` answers "is this palette
  // docked?" out of this state, and it is asked while the memo below is running.
  const [pals, setPals] = useState<Record<string, boolean>>(() => {
    try {
      const raw = localStorage.getItem(PAL_KEY);
      if (raw) return { ...PAL_DEFAULT, ...JSON.parse(raw) };
    } catch { /* a corrupt value must not stop the studio opening */ }
    return PAL_DEFAULT;
  });
  // ⚠⚠ ABOVE THE MEMO THAT READS IT, for exactly the reason the palettes are — the handler table
  // is built before `useAutoCadChrome` runs, so a workspace kept in `cfg` would answer "am I
  // current?" one render late and the radio tick would lag a click behind the ribbon.
  // ⚠ Persisted, because AutoCAD remembers it: a drafter who works in 3D Modeling should not
  // have to re-choose it every morning. Its own key, so a corrupt chrome blob cannot take the
  // workspace down with it.
  const [qsel, setQSel] = useState(false);
  const [findOpen, setFindOpen] = useState(false);
  // ⚠⚠ ABOVE THE MEMO THAT READS THEM — see the header. `handlers.ts` asks whether the import
  // dialog is already up and whether a layer walk is in progress WHILE the handler memo runs,
  // so a `useState` declared below it would be in the temporal dead zone and blank the page.
  const [layerDlg, setLayerDlg] = useState<LayerDlg | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [pdfOpen, setPdfOpen] = useState(false);
  /** Which Solid-tab body the dialog is making, or "" when it is shut. */
  const [bodyKind, setBodyKind] = useState("");
  /** The attribute window: "" shut, "defs" for BATTMAN/ATTDEF, "values" for EATTEDIT. */
  const [attrTab, setAttrTab] = useState<"" | "defs" | "values">("");
  /** The Parameters Manager — PARAMETERS, and where a DIMCONSTRAINT's value is edited. */
  const [paramsOpen, setParamsOpen] = useState(false);
  /** The open block definition — BEDIT. `null` when no block is being edited.
   *
   * ⚠ Read from the SERVER, not held here alone: the session lives on the exploded shapes, so a
   * reload, a second tab or a crashed browser all find the same answer. Local state that
   * disagreed with the drawing would strand geometry nobody could re-block. */
  const [bedit, setBedit] = useState<{ block: number; name: string; code: string } | null>(null);
  /** Bumped after a stored 3D operation, to make the viewport re-read the payload.
   *
   * ⚠ A REMOUNT rather than a reload: `Model3D` keeps `reload` on its internal ref and does not
   * report it in `actions`, so a shell cannot ask for one. Bumping the key is the one lever
   * that exists from out here, and it costs a rebuild of the scene — acceptable after a boolean,
   * which is not something you press twice a second. */
  const [modelRev, setModelRev] = useState(0);
  /** VPORTS — "" is one viewport, "2v" is the drawing beside a live 3D view.
   *  ⚠ Remembered per browser like the palettes and the workspace, because AutoCAD remembers a
   *  viewport configuration and a drafter who works split should not re-split every morning.
   *  ⚠ Above the memo, like everything else `handlers.ts` reads while it is building. */
  const [vports, setVportsRaw] = useState<string>(() => {
    try { return localStorage.getItem(VP_KEY) || ""; } catch { return ""; }
  });
  /** What Join restores — AutoCAD's VPORTS ▸ Restore is "the last one you had". */
  const lastVp = useRef<string>("2v");
  const setVports = (v: string) => {
    if (v) lastVp.current = v;
    setVportsRaw(v);
    try { localStorage.setItem(VP_KEY, v); } catch { /* private mode */ }
  };
  /** LAYERP — one step of "put the layers back", which is the entire command.
   *
   * ⚠ A ref rather than state: no button renders from the STACK, only from whether it is empty,
   * and holding it in state would rebuild all 450 handlers on every layer change. The emptiness
   * IS in the signature below, so Previous greys and ungreys correctly.
   * ⚠ Bounded at 20. AutoCAD's LAYERP is a single step; a stack is strictly kinder, and an
   * unbounded one is a snapshot of every layer in the drawing per click, forever.
   */
  const layerBack = useRef<{ id: number; visible: boolean; locked: boolean }[][]>([]);
  const [backDepth, setBackDepth] = useState(0);

  /** Set `visible` / `locked` / `printable` on named layers, remembering what LAYERP restores.
   *
   * ⚠⚠ **A LAYER LIVES ON THE SERVER, SO THIS IS A WRITE AND A RE-READ, NOT A `setState`.** The
   * canvas draws `plan.layers` as the page fetched them; changing a copy here would look right
   * until the next reload and then silently revert. `ShellContext.reload`'s own banner is about
   * exactly this — a shell that writes through an endpoint must call it or the change is
   * invisible, which is indistinguishable from the button being dead.
   */
  const patchLayers = (names: string[], patch: any, remember = true): number => {
    const all: any[] = ctx.plan?.layers || [];
    const hit = all.filter((l) => names.includes(l.name));
    if (!hit.length) return 0;
    if (remember) {
      layerBack.current = [...layerBack.current.slice(-19),
        all.map((l: any) => ({ id: l.id, visible: !!l.visible, locked: !!l.locked }))];
      setBackDepth(layerBack.current.length);
    }
    ctx.run("acad-layer", async () => {
      for (const l of hit) await designLayersApi.update(l.id, patch);
      await ctx.reload();
    });
    return hit.length;
  };
  /** Wait for the studio's autosave to land before touching the layer ROW on the server.
   *
   * ⚠⚠ **THIS IS A REAL BUG THAT SHIPPED FOR AN HOUR AND THE UI SAID IT WORKED.** LAYMRG moves
   * the geometry in the LOCAL model (which autosaves a moment later) and then deletes the empty
   * layer through the API. Deleting first wins the race: `DesignShape.layer` is `SET_NULL`, so
   * the three shapes lost their layer, the save wrote them back with the layer they now had —
   * none — and the transcript cheerfully said *"3 objects → Z-PROBE-DST, layer Z-PROBE-SRC
   * deleted"*. The screen was right, the database was wrong, and only reading the rows found it.
   *
   * ⚠ Waits for `dirty` to RISE first. `mutate` is a `setState`, so polling immediately would
   * see the pre-move `dirty === false` and conclude the save had already happened.
   * ⚠ Bounded, and the caller is told: a delete that goes ahead anyway after a save that never
   * landed is the same bug with a longer fuse.
   */
  const afterSaved = async (): Promise<boolean> => {
    const st0 = () => canvas.current.st || {};
    for (let i = 0; i < 20 && !st0().dirty && !st0().saving; i++) {
      await new Promise((r) => setTimeout(r, 50));
    }
    if (!st0().dirty && !st0().saving) return true;      // nothing moved; nothing to wait for
    for (let i = 0; i < 120; i++) {
      if (!st0().dirty && !st0().saving) return true;
      await new Promise((r) => setTimeout(r, 250));
    }
    return false;
  };

  const layerPrev = (): boolean => {
    const snap = layerBack.current.pop();
    setBackDepth(layerBack.current.length);
    if (!snap) return false;
    ctx.run("acad-layer", async () => {
      for (const q of snap) await designLayersApi.update(q.id,
        { visible: q.visible, locked: q.locked });
      await ctx.reload();
    });
    return true;
  };
  const [ws, setWs] = useState<string>(() => {
    try { return localStorage.getItem(WS_KEY) || "ws.drafting"; } catch { return "ws.drafting"; }
  });
  const showPalette = (id: string, on?: boolean) => setPals((cur) => {
    const n = { ...cur, [id]: on === undefined ? !cur[id] : on };
    try { localStorage.setItem(PAL_KEY, JSON.stringify(n)); } catch { /* private mode */ }
    return n;
  });

  // ⚠⚠ ABOVE THE MEMO THAT READS IT, like `pals` — `handlers.ts` answers PLOT, PUBLISH and
  // PREVIEW out of this state while that memo is running. See this file's header.
  // ── the camera: "plan" is the drawing canvas, anything else is the 3D viewport ──────────
  //
  // ⚠⚠ **AUTOCAD HAS ONE MODEL SPACE, AND TOP VIEW IS THE PLAN.** There is no "3D tab" in
  // AutoCAD and there must not be one here: View ▸ 3D Views ▸ Top IS this drawing, and every
  // other standard view is the same model from a different camera. So `camera === "plan"`
  // leaves the `viewport` slot empty and the studio draws its own canvas; anything else hands
  // the whole canvas area to `Model3D`. Offering a separate 3D tab would be our filing system
  // leaking into a product that does not have one.
  //
  // ⚠ NOT persisted. A drafter opening a drawing expects the plan, not wherever the last
  // session left the camera — and unlike a ribbon tab, a camera you did not choose is
  // disorienting rather than merely surprising.
  // ── model space or a layout ──────────────────────────────────────────────────────────────
  // ⚠ AutoCAD's layout tabs ARE the drawing's layouts. `space` is null in Model and the page's
  // id in paper space; the sheet composer already draws the paper at true proportion with the
  // real scene inside each viewport, which is what paper space IS — so it is hosted rather than
  // rebuilt, the same decision as the Properties palette and the Layer Properties Manager.
  const [space, setSpace] = useState<number | null>(null);
  const [pages, setPages] = useState<{ id: number; name: string }[]>([]);
  const [camera, setCamera] = useState<string>("plan");
  const [m3d, setM3d] = useState<any>(null);
  // ⚠ AutoCAD's VIEW manager. The list itself lives in `Model3D` (fetched per plan and stored
  // server-side), so this is only whether the dialog is open and which name is current — a
  // second copy of the list would drift the moment a scene was saved from the other interface.
  const [viewMgr, setViewMgr] = useState(false);
  const [testDrive, setTestDrive] = useState(false);
  const [scene, setScene] = useState("");
  // ⚠ The 3D view has to FETCH and parse the model before a tool can be armed, and now it also
  // has to fetch its own chunk. So an action issued while it is still loading is QUEUED rather
  // than dropped — otherwise "Visual Styles ▸ Hidden" pressed one second after Orbit silently
  // does nothing, which reads as a broken menu item.
  const pending = useRef<((a: any) => void) | null>(null);
  const wantCam = useRef<string | null>(null);
  /** ⚠⚠⚠ **A QUEUED ACTION FOR A VIEWER THAT NEVER LOADS IS A COMMAND THAT DOES NOTHING IN
   *  SILENCE.** `Model3D` is a deliberate stub and reports itself as one — see `NOT_BUILT` there.
   *  Six commands routed through here and were accepted, echoed and dropped: VISUALSTYLES,
   *  ANALYSISZEBRA, ANALYSISDRAFT, 3DWALK, CONVTOSURFACE and PRESSPULL. `command_survey.py`
   *  called all six SILENT, which is its own bucket for exactly this and could not say why.
   *  ⚠ Said ONCE per attempt and with the stub's OWN sentence, so the panel and the command line
   *  cannot disagree about the same missing feature. */
  /** ⚠ `stub` was the whole viewer being absent; `modelling` is the viewer being REAL and the
   *  kernel behind these commands not existing. Both are a sentence the viewer owns, and the
   *  one that is set is the one said — see `three/Model3D.tsx`. */
  const cannot3d = () => m3d?.stub || m3d?.modelling || null;
  // ⚠⚠ **A PICK IN 3D IS A SELECTION, NOT A VIEWER STATE.** Without this the object is
  // highlighted in the viewport and the Properties palette beside it still says "no selection" —
  // two answers to one question, which is the defect this file's own notes keep recording.
  const pick3d = m3d?.tool?.pick;
  React.useEffect(() => {
    if (camera === "plan") return;                        // 2D owns its own selection
    ctx.pick3d?.(pick3d ? [pick3d] : []);
  }, [pick3d && pick3d.handle, camera]);
  const sayStub = () => {
    pending.current = null;
    chromeRef.current?.say(`  ${cannot3d()}`);
    return true;
  };
  const go3d = (fn?: (a: any) => void, cam?: string) => {
    // ⚠ A camera change is still honoured — the ViewCube and the notice are the answer to
    // ORBIT. It is the queued ACTION that has nowhere to go.
    if (cannot3d() && fn) { if (cam) setCamera(cam); sayStub(); return; }
    if (camera === "plan") {
      wantCam.current = cam || "iso";
      if (fn) pending.current = fn;
      setCamera(cam || "iso");
      return;
    }
    if (cam) { m3d?.actions?.view?.(cam); setCamera(cam); wantCam.current = null; }
    if (fn && m3d?.actions) { fn(m3d.actions); pending.current = null; }
    else if (fn) pending.current = fn;
  };
  useEffect(() => {
    // ⚠ The queue is DRAINED into a refusal when the viewer turns out to be a stub — the first
    // command of a session arrives before the panel has mounted, so it is queued before there is
    // anything to report and would otherwise sit there for good.
    if (camera !== "plan" && cannot3d() && pending.current) { sayStub(); return; }
    if (camera === "plan" || !m3d?.actions || m3d?.tool?.loading) return;
    if (wantCam.current) { m3d.actions.view(wantCam.current); wantCam.current = null; }
    if (pending.current) { pending.current(m3d.actions); pending.current = null; }
  }, [camera, m3d]);

  const [plot, setPlot] = useState<"drawing" | "set" | null>(null);
  const [plotting, setPlotting] = useState(false);
  const [sheets, setSheets] = useState<number | null>(null);
  const [plotCfg, setPlotCfgRaw] = useState<PlotCfg>(() => {
    try {
      const raw = localStorage.getItem(PLOT_KEY);
      // Merged over the defaults, never raw — same reason as the palettes above.
      if (raw) return { ...DEFAULT_PLOT, ...JSON.parse(raw) };
    } catch { /* a corrupt value must not stop the studio opening */ }
    return DEFAULT_PLOT;
  });
  const setPlotCfg = (patch: Partial<PlotCfg>) => setPlotCfgRaw((c) => {
    const n = { ...c, ...patch };
    try { localStorage.setItem(PLOT_KEY, JSON.stringify(n)); } catch { /* private mode */ }
    return n;
  });

  // ⚠ Asked ONCE per drawing and only in AutoCAD mode. PUBLISH has to know whether there is a
  // composed set at all — "publish" that quietly produced a single A3 would be the wrong
  // deliverable under the right button name.
  // ⚠ A host that HAS layouts (pdcad reads them out of the DXF) hands them over rather than
  // having them fetched — see `ShellContext.layouts`. The fetch below is for the design app,
  // whose sheets are composed elsewhere and are not part of the document.
  const own = ctx.layouts;
  // ⚠ Which DRAWING these layouts belong to, so a new one takes `ctx.openSpace` and a re-render
  // of the same one keeps the tab the drafter chose. The tab ids are INDICES, so "still valid"
  // is not enough on its own: layout 0 exists in nearly every drawing, and keeping it would
  // silently carry one file's tab into the next.
  const spaceFor = useRef<number | null>(null);
  //: The last space NAME the host was told — declared HERE because the tab effect above reads
  //: it to tell a tab click's echo apart from the host moving on its own.
  const told = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    if (!own) return;
    setPages(own);
    setSheets(own.length);
    // ⚠⚠ **A LAYOUT LIST THAT CANNOT ANSWER `openSpace` YET MUST NOT ANSWER "model space".**
    // `layouts` is [] on the render before the host's fetch resolves, and [] is truthy — so the
    // first pass found no match, fell back to null, and told the host to go to model space. The
    // right tab lit a beat later and the wrong display list had already been fetched. Leaving
    // the tab alone until the list can answer is the difference.
    const want = ctx.openSpace ? own.find((q) => q.name === ctx.openSpace)?.id : null;
    if (want === undefined) return;
    const fresh = spaceFor.current !== ctx.planId;
    spaceFor.current = ctx.planId;
    // ⚠⚠⚠ **THE HOST CAN CHANGE SPACE WITHOUT THE TAB, AND THEN NOTHING IS ON SCREEN.** This
    // effect deliberately keeps the tab the drafter chose over whatever `openSpace` says —
    // right, when `openSpace` is only ever the echo of a tab click. It is not: `Studio.goSpace`
    // is also called by the app itself, by `runLayout` when a layout is deleted and by
    // `__pdcadSpace`. The studio then holds `space = null` while this still holds the sheet's
    // id, so `chrome.viewport` is built as the `SheetComposer` STUB and `Studio` renders it
    // (its condition is `space === null`, which is now true): canvas unmounted, "Sheet composer
    // — Phase 3" over a drawing with 1,510,938 polylines, and the command line saying "model
    // space" underneath it. Measured on drawing 10951 — `canvas: NONE, composer: true`.
    // ⇒ `told` is the last name WE gave the host. If `openSpace` is not that, the host moved on
    // its own and the tab follows it; if it is, this is the echo of a tab click and the
    // drafter's choice stands. ⚠ Not a loop: the second effect dedupes by name.
    const adopt = fresh || ctx.openSpace !== told.current;
    setSpace((cur) => (!adopt && cur !== null && own.some((q) => q.id === cur) ? cur : want));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `told` is a ref, not a trigger
  }, [own, ctx.openSpace, ctx.planId]);

  const tell = ctx.onSpace;
  //: The last space NAME the host was told. ⚠⚠ **THE EFFECT FIRES ON `own` TOO, and `own`
  //: changes while a drawing is opening** — so the host was told "model space" once more, from
  //: a tab state that had not caught up, after it had already loaded the sheet `openSpace`
  //: named. The tab ended up right and one wrong display list had been fetched on the way, with
  //: "  model space" in the transcript as the only sign. Deduping by name is the fix: the same
  //: answer twice is not news.
  useEffect(() => {
    if (!tell) return;
    const name = space === null
      ? null
      : ((own || pages).find((p) => p.id === space)?.name ?? null);
    if (told.current === name) return;
    told.current = name;
    tell(name);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `pages` is a fallback, not a trigger
  }, [space, own, tell]);

  useEffect(() => {
    if (own) return;
    if (!ctx.active || !ctx.planId) return;
    let dead = false;
    designSheets(ctx.planId)
      .then((r: any) => {
        if (dead) return;
        const ps = (r?.pages || []).map((q: any, i: number) => ({
          id: q.id, name: q.name || q.code || `Layout${i + 1}` }));
        setPages(ps);
        setSheets(ps.length);
        // ⚠ A layout tab for a page that has since been deleted would be a tab onto nothing.
        setSpace((cur) => (cur && ps.some((q: any) => q.id === cur) ? cur : null));
      })
      .catch(() => { if (!dead) { setSheets(null); setPages([]); } });
    return () => { dead = true; };
  }, [ctx.active, ctx.planId, own]);

  /** Plot with the remembered settings. `preview` opens it in a tab; otherwise it downloads.
   *
   * ⚠ The dialog's fields go STRAIGHT to `/design/plans/<id>/export/` — `sheet`, `orientation`
   * and `scale` are `to_pdf`'s own arguments and the four content flags are its `want()` keys.
   * Nothing here is translated, so a field that looks wrong on the sheet is wrong in one place. */
  const doPlot = (what: "drawing" | "set", preview: boolean) => {
    const name = (ctx.plan?.name || `plan-${ctx.planId}`)
      .replace(/[^\w-]+/g, "-").slice(0, 60);
    // ⚠⚠ **PLOT WHAT IS ON SCREEN.** A drafter looking at layout GA-01 who presses PLOT means
    // that sheet, and this sent model space fitted to A3 instead — a plausible-looking PDF of
    // the wrong thing, which is worse than an error. A layout plots at ITS OWN paper size and
    // 1:1 by definition, so `sheet`, `orientation` and `scale` are ignored by the server when
    // one is named; the dialog says so rather than leaving three live-looking fields lying.
    const onSheet = space === null
      ? null : ((own || pages).find((p) => p.id === space)?.name ?? null);
    const opts: any = what === "set" ? { set: 1 } : {
      sheet: plotCfg.sheet,
      orientation: plotCfg.orientation,
      scale: plotCfg.fit ? "auto" : plotCfg.scale,
      furniture: plotCfg.furniture, dimensions: plotCfg.dimensions,
      schedules: plotCfg.schedules, colour: plotCfg.colour,
      space: onSheet,
    };
    setPlotting(true);
    ctx.run("acad-plot", async () => {
      try {
        // ⚠ `designExport` opens a PDF in a tab when no filename is given and downloads it when
        // one is — which is exactly AutoCAD's Preview versus Plot, so no second code path.
        await designExport(ctx.planId, "pdf", opts, preview ? undefined : `${name}.pdf`);
        chromeRef.current?.say(
          `  ${preview ? "preview" : "plot"} · ${what === "set" ? `set, ${sheets} sheets`
            : onSheet ? `layout "${onSheet}", its own paper at 1:1`
            : `${plotCfg.sheet} ${plotCfg.orientation}, ` +
              `${plotCfg.fit ? "fit to paper" : `1:${plotCfg.scale}`}`}`);
      } finally { setPlotting(false); }
    });
    if (!preview) setPlot(null);
  };

  /** ⚠ A ref because `doPlot` is defined ABOVE `chrome` and needs to write to its transcript.
   *  Reading `chrome` directly here would be the temporal-dead-zone crash in this file's header. */
  const chromeRef = useRef<any>(null);

  // ⚠⚠ **THE HANDLER TABLE MUST NOT REBUILD ON A CURSOR MOVE, AND IT USED TO.** `PlanStudio`
  // reports its whole state — including `cursor` — on every pointer move, so `A`, `T` and `st`
  // are fresh objects sixty times a second. With those in the dependency list, all 450 handlers
  // were rebuilt, `useAutoCadChrome`'s merge re-ran, and every one of the ~70 ribbon buttons
  // reconciled, for a mouse that had moved four pixels. Measured before this change: 3.48 ms of
  // main-thread script per move.
  //
  // The fix is two halves and BOTH are needed:
  //  1. the dependency list is a SIGNATURE of the values that change what the table LOOKS like —
  //     the layer in the picker, the ticked snaps, whether anything is selected — and nothing
  //     else. A cursor position changes none of them.
  //  2. the table's closures reach the canvas through a REF, so a handler built ten seconds ago
  //     still calls today's `runCmd`. Without this, half the mode would silently act on a stale
  //     selection — a far worse bug than the one being fixed, and one no screenshot would show.
  const canvas = useRef<{ A: any; T: any; st: any }>({ A, T, st });
  canvas.current = { A, T, st };
  const facade = useRef<{ A: any; T: any; st: any } | null>(null);
  if (!facade.current) {
    const live = (k: "A" | "T" | "st") =>
      new Proxy({}, { get: (_t, prop) => (canvas.current[k] as any)?.[prop] });
    facade.current = { A: live("A"), T: live("T"), st: live("st") };
  }

  // ⚠⚠ **A FIELD THE HANDLER TABLE READS MUST BE IN THIS SIGNATURE.** This is the shell's twin
  // of the banner on `PlanStudio`'s report effect, and it failed the same way: the four drafting
  // toggles were reported by the canvas, read by `st.grid`/`st.ortho`/`st.polar`/`st.snapmode`
  // for their `on` state and their tooltips, and named NOWHERE here — so the memo never rebuilt,
  // the buttons never lit, and picking 45° on the polar drop-up left the tooltip saying 15°.
  // The BEHAVIOUR was correct throughout, which is what made it worth catching: the canvas was
  // locking to the axis while the status bar showed the toggle off.
  const sig = [mode, ctx.planId, T?.drawLayer, T?.underlay, T?.sides, T?.dimKind, T?.clipCount,
               (T?.snaps || []).join(","), !!st?.selected,
               T?.showGrid, T?.snapMode, T?.ortho, T?.polar, T?.polarAng, T?.pdmode,
               // ⚠ Added 2026-08-31 with LWT, DYNIN and ISOLATE — and forgotten for one build,
               // which reproduced the fault the banner above describes EXACTLY: three toggles
               // that worked and three buttons that never lit. The banner was written a few
               // hours earlier, about the same three lines. Read it before adding a field.
               T?.lwt, T?.dynin, T?.otrack, T?.isolated,
               // ⚠⚠ NOT `layers.length` — that was here alone and it is the WRONG signature for
               // the layer tools. Turning A-WALL off changes no COUNT, so the memo never
               // rebuilt, LAYOFF's own button never lit and LAYON stayed live with nothing left
               // to turn on. This is the third time this exact field-missing-from-`sig` bug has
               // been shipped in this file; read the banner above before adding a handler that
               // reads something new.
               (ctx.plan?.layers || []).map((l: any) =>
                 `${l.id}${l.visible ? 1 : 0}${l.locked ? 1 : 0}`).join(","),
               // ⚠⚠ FOURTH TIME. UFRAME and ADJUST are `on:` from these two, so without them
               // here the underlay would go monochrome and the button would never light — the
               // same shape as the four drafting toggles, then LWT/DYNIN/ISOLATE, then the
               // layers. Read the banner above before adding a handler that reads a new field.
               // ⚠ ISODRAFT's toggle and its plane name are both read by `st.iso` — the fifth
               // entry in this signature's history of "the behaviour worked and the button
               // never lit".
               T?.iso, T?.isoPlane,
               // ⚠ …and SELECTIONCYCLING, the SIXTH. Its handler reads `T?.cycling`
               // for both its lit state AND its note, so leaving it out gives a toggle
               // that flips the canvas and never changes on screen.
               T?.cycling,
               // ⚠ …and DIMBREAK's Add/Remove, which reads both counts.
               T?.dimsBroken, T?.dimsLinear,
               // ⚠ …and the drawing's own DIMBREAK flag, which is what the button reads
               // for its lit state now that the setting lives on the plan.
               ctx.plan?.dim_break,
               // ⚠ …and the arc/circle family's given, the EIGHTH — `acadOfMode` reads it to
               // decide which of eleven flyout buttons is lit, and `draw.arc.cont` reads
               // `canContinueArc` for both its live state AND its note.
               T?.arcGiven, T?.circGiven, T?.canContinueArc,
               // ⚠ …and INFER CONSTRAINTS, the TENTH. `st.infer` reads it for its lit state, so
               // without it here the toggle would flip the canvas and never change on screen —
               // the exact failure this list's banner is about. Read that banner first.
               T?.inferCon,
               // ⚠ …and the model-space background, the SEVENTH. Options ▸ Display reads
               // `ui.canvasbg`'s `on` to draw its tick, so without this the box would flip the
               // canvas to black and then show itself unticked on the next open.
               T?.dark,
               T?.underlayFrame, T?.underlayMono, T?.hasUnderlay,
               // ⚠ …and the clip, for the same reason. Remove Clip is greyed on `hasClip`.
               JSON.stringify(ctx.plan?.underlay_clip || null),
               // ⚠ …and the Solid tab's dialog, which every body command reads for its
               // own lit state — see the banner at the top of this list.
               bodyKind,
               // ⚠ …and the attribute window, plus WHETHER ONE BLOCK IS SELECTED, which is what
               // BLOCK, BATTMAN and EATTEDIT read for both their live state and their notes.
               attrTab, st?.selected?.kind, st?.selected?.obj?.id,
               // ⚠ …and the Parameters Manager, which every DIMCONSTRAINT reads for its lit
               // state, plus the selection count the five of them read for their notes.
               paramsOpen,
               (st?.selShapes || []).length,
               // ⚠ …and the open block definition, the NINTH. BEDIT is lit while a block is
               // open, and Save Block / Discard exist ONLY then — three buttons whose whole
               // state is this one field. Read the banner at the top before adding a handler
               // that reads something new.
               bedit?.block,
               // ⚠ …and per-object TRANSPARENCY, the ELEVENTH. `prop.transparency` reads
               // `alphaOf()` for the value shown in its box and `st.transparency` reads
               // `showAlpha` for its lit state, so leaving either out gives a control that sets
               // the drawing and never moves itself — which is this list's whole history.
               T?.showAlpha, T?.alpha,
               // ⚠ …and the selection's own PEN, the TWELFTH. The three Properties boxes read
               // `T.pen` for the value each one shows, so without it here they would sit on
               // ByLayer forever while the drawing changed underneath them.
               JSON.stringify(T?.pen || null),
               backDepth, !!layerDlg, importOpen, pdfOpen, vports].join("|");

  const handlers = useMemo(() => buildAcadHandlers({
    mode, setMode: ctx.setMode,
    A: facade.current!.A, T: facade.current!.T, st: facade.current!.st,
    plan: ctx.plan, planId: ctx.planId, nav: ctx.nav,
    run: ctx.run, warn: ctx.warn,
    showPalette,
    paletteOpen: (id) => !!pals[id],
    options: ctx.openPalette,
    exportAs: ctx.exportAs, print: ctx.print,
    openPlot: setPlot, plotNow: doPlot, sheets,
    camera, setCamera, go3d, m3d,
    space, setSpace, pages,
    openViews: () => setViewMgr(true),
    openTestDrive: () => setTestDrive(true),
    openQSelect: () => setQSel(true),
    openFind: () => setFindOpen(true),
    setUnderlayClip: (r: number[] | null) => {
      // ⚠ Straight to the plan and then a re-read, like the layer tools: the canvas draws
      // `plan.underlay_clip` as the page fetched it, so a local edit would look right until
      // the next reload and then revert.
      ctx.run("acad-clip", async () => {
        await designPlansApi.update(ctx.planId, { underlay_clip: r });
        await ctx.reload();
        chromeRef.current?.say(r ? `  underlay clipped to ${Math.round(r[2])} × `
                                   + `${Math.round(r[3])} mm`
                                 : "  underlay clip removed");
      });
    },
    layers: ctx.plan?.layers || [],
    patchLayers, layerPrev, canLayerPrev: backDepth > 0,
    openLayerDlg: setLayerDlg,
    openImport: () => setImportOpen(true),
    openPdfImport: () => setPdfOpen(true),
    makeBody: (k: string) => setBodyKind(k),
    bodyKind,
    openAttrs: (t: "defs" | "values") => setAttrTab(t),
    attrTab,
    openParams: () => setParamsOpen(true),
    paramsOpen,
    /** DIMCONSTRAINT — constrain the selected shape, HOLDING what it already measures.
     *
     * ⚠⚠ Creating one must not MOVE anything. AutoCAD's new dimensional constraint takes the
     * size the geometry already has; one that snapped to a default the moment you applied it
     * would be indistinguishable from a bug. The server measures and stores that.
     * ⚠ Vertex 0 → vertex 1 of the picked shape: this canvas has no way to pick a VERTEX, so
     * the first two of the shape it is given is the only unambiguous reading. A circle takes
     * its radius instead and needs no second point.
     */
    addConstraint: (kind: string) => {
      // ⚠⚠ **HANDLES, NOT NUMBERS — AND `Number()` SILENTLY ATE MOST SELECTIONS.** `selShapes`
      // is a list of DXF HANDLES, which are HEX: `Number("1A3")` is NaN, so the old filter threw
      // away every handle with a letter in it and kept the ones that happened to be all digits.
      // On a real drawing that is most of the selection, and it would have read as "constraints
      // only work on some objects". Latent until now only because the endpoint it called was a
      // rejecting stub.
      const ids = (facade.current?.st?.selShapes || [])
        .map((h: any) => String(h)).filter(Boolean);
      if (!ids.length) { ctx.warn("Select a line, a polyline or a circle first."); return; }
      ctx.run("acad-con", async () => {
        const r = await designParametricPost(ctx.planId, {
          constraint: { kind, a: ids[0], b: ids.length > 1 ? ids[1] : ids[0],
                        a_ix: 0, b_ix: ids.length > 1 ? 0 : 1 } });
        const made = (r?.constraints || []).find((c: any) => c.id === r.id);
        chromeRef.current?.say(`  ${made?.name || "constraint"} = ${made?.expr} `
          + "— open Parameters to change it and the geometry follows");
        setParamsOpen(true);
      }, "Constraint added");
    },
    /** DELCONSTRAINT — the selected objects' constraints, or all of them.
     *
     * ⚠ Nothing selected means ALL, which is AutoCAD's behaviour and is destructive enough to
     * say out loud in the prompt line afterwards — the count is the only thing that tells a
     * drafter they cleared the drawing rather than the one wall they had in mind.
     * ⚠ Named parameters survive either way. See `parametric.remove`.
     */
    delConstraints: () => {
      const ids = (facade.current?.st?.selShapes || []).map((h: any) => String(h)).filter(Boolean);
      ctx.run("acad-delcon", async () => {
        const r = await designParametricDel(
          ctx.planId,
          ids.length ? `handles=${ids.map(encodeURIComponent).join(",")}` : "all=1");
        await ctx.reload();
        chromeRef.current?.say(`  ${r?.deleted || 0} constraint(s) deleted`);
      }, "Constraints deleted");
    },
    constraintBar: (show: boolean) => {
      ctx.run("acad-conbar", async () => {
        const r = await designParametricPost(ctx.planId, { op: show ? "showall" : "hideall" });
        chromeRef.current?.say(`  ${r?.changed || 0} constraint bar(s) `
          + (show ? "shown" : "hidden"));
        await ctx.reload();
      }, show ? "Constraints shown" : "Constraints hidden");
    },
    // ⚠⚠ **BLOCK CONSUMES THE SELECTION AND LEAVES AN INSTANCE IN ITS PLACE** — see `blocks.py`,
    // which has done this since it was first built. It was never reachable from here; the
    // note on these commands said the library is curated, which stopped being the whole truth
    // the day `create_from_selection` landed.
    makeBlock: (toLibrary: boolean) => {
      const ids = (facade.current?.st?.selShapes || []).map((i: any) => Number(i))
        .filter((n: number) => Number.isFinite(n));
      if (!ids.length) {
        ctx.warn("Select drawn geometry first — a block is made FROM something.");
        return;
      }
      // eslint-disable-next-line no-alert -- an honest stand-in; the
      // block is renamed in the Blocks palette afterwards.
      const name = window.prompt(toLibrary ? "Write block to the library as" : "Block name", "");
      if (!name || !name.trim()) return;
      ctx.run("acad-block", async () => {
        const r = await designMakeBlock(ctx.planId, {
          name: name.trim(), shapes: ids,
          // ⚠ WBLOCK's only honest meaning here: there are no .dwg files, so "write out" means
          // "put it in the shared library and KEEP the geometry on this drawing" — the source
          // survives, which is exactly what writing a copy to a file does.
          category: "retail", keep_source: toLibrary });
        await ctx.reload();
        chromeRef.current?.say(`  ${name.trim()} — ${ids.length} object(s)`
          + (toLibrary ? ", geometry kept" : ", replaced by one instance")
          + ` (block #${r?.block?.id ?? r?.id ?? "?"})`);
      }, "Block created");
    },
    // ── BEDIT ────────────────────────────────────────────────────────────────────────────────
    //
    // ⚠⚠ **THIS COMMAND WAS GREY BEHIND A REASON MY OWN COMMIT FALSIFIED.** It read "a block's
    // body is authored SVG, curated in the library rather than edited in a drawing" — true of
    // the 630 catalogue symbols, and false the moment BLOCK/WBLOCK let a designer make one from
    // a selection. A block somebody drew on THIS plan is curated by nobody.
    //
    // ⚠ Editing changes every placed copy. That is what a block IS, and it is also why a
    // catalogue symbol refuses — it is shared with every other drawing in the office, so the
    // server sends you to Make Unique instead of quietly changing another job's reception desk.
    beditOpen: () => {
      const inst = facade.current?.st?.selected;
      if (!inst || inst.kind !== "block") {
        ctx.warn("Select a placed block to edit its definition.");
        return;
      }
      ctx.run("acad-bedit", async () => {
        const r = await designBlockEditOp(ctx.planId, { op: "open", instance: inst.id });
        setBedit({ block: r.block, name: r.name, code: r.code || "" });
        await ctx.reload();
        chromeRef.current?.say(`  ${r.name} — ${r.shapes.length} object(s) on the drawing. `
          + "Edit them, then Save Block.");
      }, "Block open for editing");
    },
    beditClose: (save: boolean) => {
      ctx.run("acad-bedit", async () => {
        const r = await designBlockEditOp(ctx.planId, { op: save ? "save" : "cancel" });
        setBedit(null);
        await ctx.reload();
        chromeRef.current?.say(save
          ? `  saved — ${r.w_mm}×${r.d_mm} mm, ${r.instances ?? 1} placed copy(s) updated`
          : "  edit discarded, the block is as it was");
      }, save ? "Block saved" : "Edit discarded");
    },
    bedit,
    // ── the Solid tab's face commands ────────────────────────────────────────────────────────
    //
    // ⚠⚠ **ALL FOUR WERE GREY FOR ONE REASON — "faces are not addressable here" — AND THAT IS
    // NOW FALSE.** `kernel.faces` addresses a face by its own centroid, so SHELL can open one,
    // SLICE can cut on one's plane and OFFSETEDGE can offset inside one.
    //
    // ⚠ SHELL and SLICE take NO second solid: a wall thickness and a plane are numbers, carried
    // in the op's `props`. That is exactly why SLICE was grey — `targets` names records and a
    // plane is not a record.
    solidFaceOp: (op: "shell" | "slice" | "imprint" | "offsetedge") => {
      go3d(async (a: any) => {
        const picked = (a.selSrcOrder?.() || []) as any[];
        if (!picked.length) {
          chromeRef.current?.say("  pick a solid in the model first");
          return;
        }
        const body: any = { op, targets: op === "imprint" ? picked.slice(1) : picked,
                            tools: op === "imprint" ? [picked[0]] : [] };
        if (op === "imprint" && picked.length < 2) {
          chromeRef.current?.say("  pick two solids — the first supplies the edges to imprint");
          return;
        }
        if (op === "shell") {
          // eslint-disable-next-line no-alert -- the honest stand-in this shell already uses for
          // BLOCK and SCALETEXT; the thickness is editable on the op afterwards.
          const t = window.prompt("Shell to what wall thickness, in mm?\n\n"
                                  + "Add face names to leave open, e.g. \"18 top\" for a tray "
                                  + "or \"18 top front\" for a carcass.", "18");
          if (!t) return;
          const bits = t.trim().split(/\s+/);
          const n = Number(bits[0]);
          if (!Number.isFinite(n) || n <= 0) { ctx.warn("A wall thickness must be a number."); return; }
          // ⚠ Faces are NAMED, not indexed. The viewport classifies faces semantically for
          // push/pull and has no notion of the kernel's centroid ordering, so a direction is
          // the one thing both ends already agree on — and it is what a drafter says out loud.
          body.props = { thickness: n, open: bits.slice(1).map((w) => w.toLowerCase()) };
        }
        if (op === "offsetedge") {
          // eslint-disable-next-line no-alert -- as above
          const t = window.prompt("Offset inside which face, and by how far?\n\n"
                                  + "e.g. \"top 50\" — top, bottom, front, back, left, right.",
                                  "top 50");
          if (!t) return;
          const [face, dist] = t.trim().toLowerCase().split(/\s+/);
          const n = Number(dist);
          if (!Number.isFinite(n) || n <= 0) { ctx.warn("Offset needs a distance in mm."); return; }
          body.props = { face, distance: n };
        }
        if (op === "slice") {
          // eslint-disable-next-line no-alert -- as above
          const ax = window.prompt("Slice on which plane — x, y or z? Add a distance, e.g. "
                                   + "\"z 1200\" to cut at 1200 mm.", "z");
          if (!ax) return;
          const [w, dist] = ax.trim().toLowerCase().split(/\s+/);
          // ⚠⚠ **THE DRAFTER NAMES A DRAWING AXIS; THE KERNEL WANTS A VIEWER ONE.** The plan is
          // y-down and the scene is y-UP with the plan's y as its z — the single conversion
          // `model3d._solid` documents. So drawing-x is scene-x, drawing-y is scene-Z, and "z"
          // means HEIGHT, which is scene-Y. Passing the drawing's own axes straight through
          // would cut the model on its side, plausibly, with nothing on screen to say so.
          const PLANE: Record<string, number[]> = {
            x: [1, 0, 0],      // across the drawing
            y: [0, 0, 1],      // up the drawing — the scene's depth
            z: [0, 1, 0],      // height above the level
          };
          const normal = PLANE[w];
          if (!normal) { ctx.warn("Pick x, y or z."); return; }
          const at = Number(dist || 0) || 0;
          body.props = { origin: normal.map((v) => v * at), normal, keep: "front" };
        }
        try {
          const r = await designSolidOpAdd(ctx.planId, body);
          const rep = (r.report || [])[0] || {};
          chromeRef.current?.say(rep.error || rep.skipped
            ? `  ${rep.error || rep.skipped}`
            : `  ${op} — ${rep.pieces ?? 0} solid(s), ${rep.triangles ?? 0} triangles`
              + `${rep.warn ? ` (${rep.warn})` : ""}`);
        } catch (e: any) {
          chromeRef.current?.say(`  ${e?.response?.data?.detail || `${op} could not be built`}`);
          return;
        }
        setModelRev((n: number) => n + 1);
      }, "iso");
    },
    // ⚠⚠ CONVTOSOLID — the command that lets a client's .skp furniture be cut by a wall.
    // `solidops` refuses triangles, so before this a designer could boolean everything the app
    // generated and nothing anybody sent them.
    meshToSolid: () => {
      go3d(async (a: any) => {
        const picked = (a.selSrcOrder?.() || []) as any[];
        if (!picked.length) { chromeRef.current?.say("  pick an imported mesh first"); return; }
        try {
          const r = await designMeshSolid(ctx.planId, picked[0]);
          chromeRef.current?.say(r.already
            ? "  already converted — it can be booleaned"
            : `  converted — ${r.triangles} triangles, `
              + `${(r.volume_mm3 / 1e9).toFixed(3)} m³. It can now be cut and united`);
        } catch (e: any) {
          chromeRef.current?.say(`  ${e?.response?.data?.detail || "that mesh will not convert"}`);
          return;
        }
        setModelRev((n: number) => n + 1);
      }, "iso");
    },
    // ── the Surface tab ──────────────────────────────────────────────────────────────────────
    //
    // ⚠ The PROFILE is the plan selection, which is the gesture this canvas can offer: draw the
    // curve, pick it, press the surface. AutoCAD picks curves in 3D; this drawing is one plane
    // and the tooltip says so.
    makeSurface: (kind: string) => {
      // ── THICKEN and SCULPT act on surfaces that already exist ──────────────────────────
      //
      // ⚠ Both are stored on the surface's own `params`, not as second records: a skin and its
      // thickness are one thing, and moving the profile has to move both. A separate row would
      // be a copy that goes stale the moment somebody drags the curve.
      // ── TRIM / UNTRIM: the operation is STORED, so untrim is not applying it ────────────
      //
      // ⚠⚠ **NOTHING IS DESTROYED BY A TRIM, WHICH IS THE ONLY REASON UNTRIM CAN EXIST.** Baking
      // the surviving piece would leave SURFUNTRIM a button that could only apologise. The
      // cutters are named on `params.trim` and the skin is re-cut on every read — the same rule
      // `DesignSolidOp` follows for a boolean.
      // ⚠ PROJECT needs BOTH a plan curve (selected) and a surface to land on, so it reads the
      // selection like the builders below rather than the surface list like trim/thicken.
      if (kind === "__project") {
        const ids = (facade.current?.st?.selShapes || []).map((i: any) => Number(i))
          .filter((n: number) => Number.isFinite(n));
        if (!ids.length) {
          ctx.warn("Select the curve to project first.");
          return;
        }
        ctx.run("acad-surface", async () => {
          const { surfaces } = await designSurfaces(ctx.planId);
          if (!surfaces?.length) {
            chromeRef.current?.say("  no surfaces to project onto — build one first");
            return;
          }
          let hit = 0;
          for (const S of surfaces) {
            const r = await designAddSurface(ctx.planId, {
              id: S.id,
              params: { ...(S.params || {}), project: { from: { m: "shape", ids } } } });
            if (!(r.report || []).some((x: any) => x.warn || x.skipped)) hit += 1;
          }
          await ctx.reload();
          setModelRev((v: number) => v + 1);
          chromeRef.current?.say(hit
            ? `  projected onto ${hit} surface(s) — the curve is drawn ON the skin`
            : "  that curve does not land on any surface from above");
        }, "Projected");
        return;
      }
      if (kind === "__trim" || kind === "__untrim") {
        ctx.run("acad-surface", async () => {
          const { surfaces } = await designSurfaces(ctx.planId);
          if ((surfaces?.length || 0) < (kind === "__trim" ? 2 : 1)) {
            chromeRef.current?.say(kind === "__trim"
              ? "  trim needs two surfaces — one to cut, one to cut with"
              : "  no surfaces on this drawing");
            return;
          }
          if (kind === "__untrim") {
            let n = 0;
            for (const S of surfaces) {
              if (!(S.params || {}).trim) continue;
              const params = { ...(S.params || {}) };
              delete params.trim;
              await designAddSurface(ctx.planId, { id: S.id, params });
              n += 1;
            }
            await ctx.reload();
            setModelRev((v: number) => v + 1);
            chromeRef.current?.say(n ? `  ${n} surface(s) restored whole`
                                     : "  nothing on this drawing is trimmed");
            return;
          }
          // ⚠ The FIRST surface is trimmed by all the others. AutoCAD asks which is which; a
          // one-line command bar cannot, and "the first one you made" is at least a rule the
          // tooltip can state rather than a guess the drafter has to discover.
          const [first, ...rest] = surfaces;
          const r = await designAddSurface(ctx.planId, {
            id: first.id,
            params: { ...(first.params || {}), trim: { by: rest.map((x: any) => x.id) } } });
          await ctx.reload();
          setModelRev((v: number) => v + 1);
          const bad = (r.report || []).find((x: any) => x.skipped || x.error);
          chromeRef.current?.say(bad ? `  ${bad.skipped || bad.error}`
                                     : `  trimmed by ${rest.length} surface(s) — Untrim restores it`);
        }, kind === "__trim" ? "Trimmed" : "Untrimmed");
        return;
      }
      if (kind === "__thicken" || kind === "__sculpt") {
        ctx.run("acad-surface", async () => {
          const { surfaces } = await designSurfaces(ctx.planId);
          if (!surfaces?.length) {
            chromeRef.current?.say("  no surfaces on this drawing — build one first");
            return;
          }
          if (kind === "__sculpt") {
            if (surfaces.length < 2) {
              chromeRef.current?.say("  sculpt needs at least two surfaces enclosing a space");
              return;
            }
            for (const S of surfaces) {
              await designAddSurface(ctx.planId,
                { id: S.id, params: { ...(S.params || {}), sculpt: true } });
            }
          } else {
            // eslint-disable-next-line no-alert -- the stand-in this shell already uses
            const t = window.prompt("Thicken the surface(s) by how many mm?", "18");
            if (!t) return;
            const n = Number(t);
            if (!Number.isFinite(n) || !n) { ctx.warn("Thickness must be a number."); return; }
            for (const S of surfaces) {
              await designAddSurface(ctx.planId,
                { id: S.id, params: { ...(S.params || {}), thicken_mm: n } });
            }
          }
          await ctx.reload();
          setModelRev((n: number) => n + 1);
          const { report } = await designSurfaces(ctx.planId);
          const bad = (report || []).find((r: any) => r.skipped || r.error);
          chromeRef.current?.say(bad
            ? `  ${bad.skipped || bad.error}`
            : kind === "__sculpt"
              ? `  sculpted ${surfaces.length} surfaces into one solid — it can now be cut`
              : `  ${surfaces.length} surface(s) given material; they are solids now`);
        }, kind === "__sculpt" ? "Sculpted" : "Thickened");
        return;
      }
      const ids = (facade.current?.st?.selShapes || []).map((i: any) => Number(i))
        .filter((n: number) => Number.isFinite(n));
      if (!ids.length) {
        ctx.warn("Draw and select the curve(s) first — a surface is made FROM something.");
        return;
      }
      const body: any = { kind, profile: { m: "shape", ids } };
      if (kind === "extrude" || kind === "offset") {
        // eslint-disable-next-line no-alert -- as above
        const h = window.prompt(kind === "extrude" ? "Extrude the curve how far, in mm?"
                                                   : "Offset the surface by how much, in mm?",
                                "1000");
        if (!h) return;
        const n = Number(h);
        if (!Number.isFinite(n) || !n) { ctx.warn("That needs a number of millimetres."); return; }
        body.h_mm = n;
      }
      if (kind === "loft" && ids.length < 2) {
        ctx.warn("A loft needs at least two curves — select them in order.");
        return;
      }
      ctx.run("acad-surface", async () => {
        const r = await designAddSurface(ctx.planId, body);
        const rep = (r.report || [])[0] || {};
        await ctx.reload();
        setModelRev((n: number) => n + 1);
        chromeRef.current?.say(rep.error || rep.skipped
          ? `  ${rep.error || rep.skipped}`
          : `  ${kind} surface #${r.id} — a skin with no thickness. THICKEN gives it material`);
      }, "Surface built");
    },
    // ── placed lights ────────────────────────────────────────────────────────────────────────
    //
    // ⚠ Placed at the SELECTION's centre when there is one, because "where" is the only thing a
    // light needs that a button cannot supply, and the drafter has just pointed at it.
    placeLight: (kind: "point" | "spot" | "distant") => {
      const st: any = facade.current?.st;
      const at = st?.selectedAt || st?.cursor || null;
      const x = Number(at?.[0]);
      const y = Number(at?.[1]);
      ctx.run("acad-light", async () => {
        const r = await designAddLight(ctx.planId, {
          kind,
          x: Number.isFinite(x) ? x : Number(ctx.plan?.width_mm || 0) / 2,
          y: Number.isFinite(y) ? y : Number(ctx.plan?.height_mm || 0) / 2,
          z: kind === "distant" ? 6000 : 2700,
          tx: Number.isFinite(x) ? x : 0, ty: Number.isFinite(y) ? y : 0, tz: 0,
          // ⚠ Illuminance at ONE METRE — see `DesignLight.intensity`. A number in scene units
          // would need seven digits to light anything.
          intensity: kind === "distant" ? 1.2 : kind === "spot" ? 6 : 3,
        });
        setModelRev((n: number) => n + 1);
        chromeRef.current?.say(`  ${kind} light #${r.id} at ${kind === "distant" ? 6000 : 2700}`
          + " mm. Drag it on the Lights palette, or edit it on Properties");
      }, "Light placed");
    },
    // ⚠ A prompt rather than a fourth dialog. The shell already uses one for BLOCK, SCALETEXT
    // and SHELL, and a named origin is one short string — a 150-line window for it would be more
    // chrome than command. It LISTS what exists first, because a manager whose only affordance
    // is typing a name you must already remember is not a manager.
    ucsManager: () => {
      const st: any = facade.current?.A;
      const now = st?.ucsNamed?.();
      const names: string[] = now?.names || [];
      const at = now?.current;
      // eslint-disable-next-line no-alert -- see above
      const typed = window.prompt(
        (names.length ? `Saved origins: ${names.join(", ")}\n\n` : "None saved yet.\n\n")
        + `Current: ${Math.round(at?.x ?? 0)}, ${Math.round(at?.y ?? 0)}`
        + `${at?.rot ? ` at ${(at.rot * 180 / Math.PI).toFixed(1)}°` : ""}\n\n`
        + "Type a name to RESTORE it, a new name to SAVE the current one,\n"
        + "or -name to delete.", "");
      if (!typed || !typed.trim()) return;
      const raw = typed.trim();
      const del = raw.startsWith("-");
      const r = st?.ucsNamed?.(del ? raw.slice(1) : raw, del ? "delete" : undefined);
      if (!r?.did) return;
      chromeRef.current?.say(`  ${r.did} "${r.name}"`
        + (r.names?.length ? ` — saved: ${r.names.join(", ")}` : ""));
    },
    lightsOff: () => {
      ctx.run("acad-light", async () => {
        const { lights } = await designLights(ctx.planId);
        for (const L of lights || []) await designDelLight(ctx.planId, L.id);
        setModelRev((n: number) => n + 1);
        chromeRef.current?.say(`  ${(lights || []).length} light(s) removed`);
      }, "Lights cleared");
    },
    delSurfaces: () => {
      ctx.run("acad-surface", async () => {
        const { surfaces } = await designSurfaces(ctx.planId);
        for (const S of surfaces || []) await designDelSurface(ctx.planId, S.id);
        await ctx.reload();
        setModelRev((n: number) => n + 1);
        chromeRef.current?.say(`  ${(surfaces || []).length} surface(s) removed`);
      }, "Surfaces cleared");
    },
    // ⚠⚠ **UNION ON THE SOLID TAB WAS THE 2D SHAPE BOOLEAN.** `pdvn.union` has always run
    // `edit._op_boolean` over `DesignShape` rows — right on the drawing canvas, and wrong on a
    // tab that only exists in the 3D Modeling workspace. Once `DesignSolidOp` landed there were
    // real solid booleans to call, and this kept cutting flat polygons — so one command meant
    // two different things depending on which tab you were standing on.
    //
    // The dispatch is BY VIEWPORT, which is AutoCAD's own rule: UNION acts on what is in front
    // of you. On the plan canvas that is shapes; in the 3D view it is walls, floors, blocks and
    // joinery bodies through OCCT.
    //
    // ⚠ ORDER IS THE COMMAND — the FIRST solid picked is the tool, the rest are targets. That is
    // AutoCAD's SUBTRACT prompt ("select solids to subtract FROM", then "select solids to
    // subtract") read the other way round, and it is the convention `solidops` stores.
    solids3d: (op: string) => {
      go3d(async (a: any) => {
        const picked = (a.selSrcOrder?.() || []) as any[];
        const needsTool = op !== "union";
        if (picked.length < (needsTool ? 2 : 1)) {
          chromeRef.current?.say(needsTool
            ? "  pick two solids in the model — the first is the one that cuts"
            : "  pick the solids to join in the model");
          return;
        }
        const body = needsTool ? { op, tools: [picked[0]], targets: picked.slice(1) }
                               : { op, targets: picked };
        try {
          const r = await designSolidOpAdd(ctx.planId, body);
          const rep = (r.report || [])[0] || {};
          chromeRef.current?.say(rep.error || rep.skipped
            ? `  ${rep.error || rep.skipped}`
            : `  ${op} — ${rep.pieces ?? 0} solid${rep.pieces === 1 ? "" : "s"}, `
              + `${rep.triangles ?? 0} triangles${rep.warn ? ` (${rep.warn})` : ""}`);
        } catch (e: any) {
          chromeRef.current?.say(`  ${e?.response?.data?.detail
                                     || "that boolean could not be built"}`);
          return;
        }
        // ⚠ The op is STORED, not its result — nothing on screen changes until the payload is
        // read again. Without this the tool reports success over an unchanged model.
        setModelRev((v) => v + 1);
      }, "iso");
    },
    // ⚠⚠ **A FLAG ON THE DRAWING, NOT A PROPERTY OF EACH DIMENSION.** Most of the dimensions
    // on these drawings are GENERATED setting-out chains, rebuilt from the walls on every read —
    // there is no row to keep a break on, so a stored break could never reach them. The flag is
    // the only place the state survives, and `dimension.add_breaks` computes the crossings at
    // read time for every row, generated and hand-added alike.
    // ⚠ The re-post afterwards is the refresh: `ctx.reload()` does not refetch dimensions, and
    // the dims POST answers with `plan_dimensions` — breaks and all.
    dimBreak: (remove: boolean) => {
      ctx.run("acad-dimbreak", async () => {
        try {
          await designPlansApi.update(ctx.planId, { dim_break: !remove });
          await ctx.reload();
          A?.reSaveDims?.();
          chromeRef.current?.say(remove
            ? "  dimension lines run through again"
            : "  dimension lines gapped where anything crosses them");
        } catch (e: any) {
          chromeRef.current?.say(`  ${e?.response?.data?.detail || "DIMBREAK failed"}`);
        }
      });
    },
    // ⚠⚠ **INTERFERE REPORTS TO THE COMMAND LINE, LIKE AUTOCAD'S DOES.** It is a question, not
    // an edit — nothing changes, nothing is undoable — so a modal that has to be dismissed
    // would be in the way of the next command. The pairs are named with their overlap in
    // LITRES: a joiner reads 2.3 litres of timber in the wrong place; 2 280 000 mm³ is the same
    // fact in a unit nobody orders material in.
    interfere: () => {
      ctx.run("acad-interfere", async () => {
        // the 3D selection when there is one, the whole model when there is not — AutoCAD
        // prompts "Select first set of objects" and takes <all> on an empty return
        // ⚠ `m3d` off the closure, not a ref: this is inside `ctx.run`, which fires from
        // a click, so the render that produced the click had the current selection.
        const sel = m3d?.tool?.sel;
        const src = sel?.model && sel?.rows ? [{ m: sel.model }] : undefined;
        chromeRef.current?.say(src ? "  checking the selection…" : "  checking the whole model…");
        try {
          const r = await designInterfere(ctx.planId, src ? { src } : {});
          const pairs = r?.pairs || [];
          chromeRef.current?.say(`  ${pairs.length} interfering pair`
                                 + `${pairs.length === 1 ? "" : "s"} in ${r?.checked ?? 0} solids`);
          pairs.slice(0, 12).forEach((q: any) => chromeRef.current?.say(
            `    ${q.a} × ${q.b} — ${(q.mm3 / 1e6).toFixed(2)} litres`));
          if (r?.note) chromeRef.current?.say(`  ⚠ ${r.note}`);
        } catch (e: any) {
          chromeRef.current?.say(`  ${e?.response?.data?.detail || "interference check failed"}`);
        }
      });
    },
    vports,
    setVports: (v: string) => {
      // ⚠⚠ THE SPLIT AND THE FULL-AREA 3D VIEW ARE THE SAME COMPONENT ON THE SAME SCENE.
      // Leaving the camera orbited would mount `Model3D` twice — two WebGL contexts and twice
      // the VRAM, for a second copy of a picture already filling the screen.
      if (v) { setCamera("plan"); setSpace(null); }
      setVports(v);
    },
    lastVports: lastVp.current,
    reload: ctx.reload,
    ws,
    setCfg: (patch) => {
      if (patch.ws !== undefined) {
        setWs(patch.ws);
        try { localStorage.setItem(WS_KEY, patch.ws); } catch { /* private mode */ }
      }
      if (patch.tab !== undefined) chromeRef.current?.set({ tab: patch.tab, ribbon: true });
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps -- `sig` IS the dependency list; see above
  }), [sig, pals, ctx.plan, sheets, plotCfg, camera, m3d, space, pages, scene, ws]);

  const chrome = useAutoCadChrome(handlers);
  chromeRef.current = chrome;
  const active = acadOfMode(mode, T?.dimKind, T?.arcGiven, T?.circGiven);

  // ⚠ The ELEMENTS are memoised, not the components. `AcadRibbon` reads `chrome.all`, `chrome.cfg`
  // and `chrome.ui` and nothing else, so an element built from an older `chrome` whose those three
  // are unchanged is still correct — including its `fire`, which closes over `chrome.all`. Wrapping
  // the components in `React.memo` instead would have achieved nothing, because `chrome` itself is
  // a fresh object on every render and would defeat every comparison.
  const ribbon = useMemo(
    () => <AcadRibbon chrome={chrome} active={active} ws={ws} />,
    [chrome.all, chrome.cfg, chrome.ui, active, ws]);
  const toolbars = useMemo(
    () => <AcadToolbars chrome={chrome} active={active} />,
    [chrome.all, chrome.cfg, chrome.ui, active]);

  // ⚠ The command line echoes a tool the KEYBOARD armed, not just one the ribbon did. The acad
  // keymap binds L, REC, C, TR and the rest at the canvas, and a drafter who types `L` there and
  // sees nothing in the command line has been told the command line is decoration.
  // ⚠ Deduped against the last transcript line, because `fire` has already echoed the ones that
  // came from a button — otherwise every ribbon click on a tool would print twice.
  const lastMode = useRef<string | null>(null);
  useEffect(() => {
    if (!ctx.active) { lastMode.current = mode; return; }
    if (lastMode.current === null || lastMode.current === mode) {
      lastMode.current = mode; return;
    }
    lastMode.current = mode;
    const hit = acadOfMode(mode, T?.dimKind);
    const cmd = hit ? BTN_BY_ID[hit]?.cmd : null;
    const line = cmd ? `_${cmd}` : null;
    if (line && chrome.hist[chrome.hist.length - 1] !== line) chrome.say(line);
  }, [mode, ctx.active]);

  // ── AutoCAD's palette shortcuts: Ctrl+0 … Ctrl+9 ────────────────────────────────────────
  //
  // ⚠⚠ **THE CATALOG DECLARED THESE AND NOTHING BOUND THEM.** Every one of these ids already
  // carried `key: "Ctrl+1"` and printed it in its tooltip and its menu row, so the interface
  // has been promising a shortcut that did nothing since the day it shipped. Measured against
  // the running app: PROPERTIES opened from the command line and from the alias CH, and Ctrl+1
  // did nothing at all — the one route a drafter coming off AutoCAD actually uses.
  //
  // ⚠ Driven off `BTN_BY_ID`, not a second list. A hand-written table of shortcuts is a table
  // that drifts from the tooltips beside it, and then the interface tells you one thing and
  // does another. Whatever the catalog says the key is, that is the key.
  //
  // ⚠⚠ **DIGITS ONLY — the Ctrl+LETTER commands are NOT ours to take.** Ctrl+Z, Ctrl+S, Ctrl+C
  // and the rest are declared here too, and binding them from this shell would fire a second
  // undo beside whichever handler already owns them. The digits are AutoCAD's palette toggles,
  // nothing else in the studio claims them, and the browser's own tab-switching is the only
  // competition — which is what `preventDefault` is for.
  //
  // ⚠ `ctx.active` gates it, because the hook runs on every render whether or not the chrome is
  // on screen — so an ungated Ctrl+1 would toggle a palette for a drafter who is not looking at
  // one. The gate stays even with a single interface: `active` is false while the 3D view owns
  // the page.
  //
  // ⚠ A grey command stays bound on purpose: `fire` prints its reason on the command line and
  // returns false. Ctrl+2 answering "DesignCenter · not built yet" is the honest response; a
  // key that is silently unbound is indistinguishable from one that is broken.
  //
  // ⚠⚠⚠ **AND THE Ctrl+LETTER KEYS, WHICH THE NOTE ABOVE LEFT TO "WHICHEVER HANDLER ALREADY
  // OWNS THEM" — AND FOR SEVEN OF THEM, NOTHING DID.** The catalog declares Ctrl+C, Ctrl+V,
  // Ctrl+X, Ctrl+N, Ctrl+P, Ctrl+Q, Ctrl+S and the three Ctrl+Shift ones, the ribbon and the
  // menus PRINT those keys beside the command, and `Studio.tsx` claims only O, Z, Y and A. So
  // each side assumed the other had them and Ctrl+C copied nothing for as long as the ribbon
  // has existed. `qa/ux_survey.py` caught it as its one MISSING check — "Ctrl+C then Ctrl+V
  // copies objects" — and a key printed in a tooltip and bound to nothing is exactly the lie
  // `sysvars.py`'s header names: a live control that does not do the thing it says.
  //
  // ⚠ `OWNED_BY_STUDIO` is the exclusion, and it is the ONLY hand-written part: those four are
  // handled in `Studio.tsx`'s own keydown, and binding them here would fire a second undo
  // beside the first. Everything else comes from the catalog, so the key that fires is by
  // construction the key the tooltip shows.
  //
  // ⚠⚠ **THE INPUT GUARD IS NOT OPTIONAL FOR LETTERS.** Ctrl+C with the caret in the command
  // line is TEXT copy, in AutoCAD and in every other program, and stealing it would break the
  // one thing a drafter does with a command they want to keep. Same guard `Studio.tsx` uses:
  // an INPUT or TEXTAREA keeps its own keys. Digits are deliberately NOT guarded — Ctrl+1
  // toggles Properties in AutoCAD whatever has focus, which is the behaviour that was already
  // here and is correct.
  //
  // ⚠ Ctrl+N and Ctrl+Q are declared and will usually never arrive: Chrome reserves them for
  // its own window and quit, and a page cannot `preventDefault` a key it is not sent. They are
  // bound anyway — it costs nothing, and they DO arrive in a standalone/kiosk window. That is a
  // browser limit, not a gap in this app, and it is the one AutoCAD key pdcad cannot promise.
  useEffect(() => {
    if (!ctx.active) return;
    const OWNED_BY_STUDIO = new Set(["z", "y", "a", "o"]);
    // ⚠⚠⚠ **Ctrl+Q IS DECLARED AND DELIBERATELY NOT TAKEN, AND THIS IS NOT A GAP.** `file.exit`
    // runs `nav("/signout")` — it signs the drafter out THAT INSTANT, with no prompt. AutoCAD's
    // QUIT asks *"Save changes to <drawing>?"* first, so binding this key would be LESS like
    // AutoCAD, not more: one fat-fingered Ctrl+Q next to Ctrl+W and a drafter is at the login
    // screen mid-drawing. It stays unbound until Exit asks. ⚠ It is also the one key Chrome
    // reserves hardest, so binding it would have looked harmless in testing and fired on
    // somebody's desktop.
    const NOT_OURS_UNTIL_IT_CONFIRMS = new Set(["q"]);
    const byKey: Record<string, string> = {};
    for (const [id, b] of Object.entries(BTN_BY_ID)) {
      const m = /^Ctrl\+(Shift\+)?([0-9A-Za-z])$/.exec((b as any)?.key || "");
      if (!m) continue;
      const letter = m[2].toLowerCase();
      if (!m[1] && (OWNED_BY_STUDIO.has(letter) || NOT_OURS_UNTIL_IT_CONFIRMS.has(letter))) continue;
      const combo = (m[1] ? "shift+" : "") + letter;
      if (!byKey[combo]) byKey[combo] = id;
    }
    const onKey = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey) || e.altKey) return;
      const letter = e.key.toLowerCase();
      const combo = (e.shiftKey ? "shift+" : "") + letter;
      const id = byKey[combo];
      if (!id) return;
      const tag = (e.target as HTMLElement)?.tagName;
      if (/[a-z]/.test(letter)) {
        if (tag === "INPUT" || tag === "TEXTAREA") return;
        if ((e.target as HTMLElement)?.isContentEditable) return;
        // ⚠⚠ **A LIVE TEXT SELECTION MEANS THE DRAFTER IS COPYING TEXT, NOT OBJECTS.** The
        // command history is a `div`, so the INPUT guard above does not cover it: selecting a
        // line of history and pressing Ctrl+C would have run COPYCLIP and copied the SELECTED
        // OBJECTS instead — and before these keys were bound at all, that gesture worked,
        // because the browser's own copy handled it. Taking a working gesture away is a
        // regression even when what replaces it is a feature. AutoCAD's text window copies text
        // the same way.
        if ((letter === "c" || letter === "x")
            && (window.getSelection()?.toString() || "").trim()) return;
      }
      e.preventDefault(); e.stopPropagation();
      chrome.fire(id);
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [ctx.active, chrome]);

  // ── the icon palette, published as CSS variables ────────────────────────────────────────
  //
  // ⚠⚠ **THE LIGHT THEME REPAINTED THE CHROME AND LEFT THE ICONS BEHIND.** `icons.tsx` chose
  // its nine colours against the dark ribbon and hard-coded them, so picking Light gave a pale
  // grey line drawing on a pale grey ribbon — measured at **1.31:1** where a glyph needs 3.0,
  // with six of seven colours failing. Nothing warned; the icons were simply not there.
  //
  // ⚠ The light values are RE-CHOSEN, not the dark ones darkened, and each clears 3.0 against
  // all four surfaces an icon sits on — ribbon #EFF1F3, panel #F7F8F9, hover #CCE4F7,
  // sunk #E4E7E9. Worst case of the eight strokes is LN_D at 3.35:1.
  //
  // ⚠ PAPER and INK are FILLS, not glyphs, so they are not held to 3.0 — they have to read as
  // a sheet and as a recess. PAPER inverts (a dark face on dark chrome becomes a white sheet on
  // light); INK stays a mid grey so a hole cut in PAPER still reads as a hole.
  //
  // ⚠ On `documentElement`, not a wrapper div: the ribbon, the menus, the flyouts, the status
  // bar and the command line are six separate SLOTS handed to `DesignStudioPage`, and there is
  // no single element this shell owns that contains them all.
  // ⚠ Removed on the way out, so leaving the drafting workspace does not leave nine stale
  // variables on the document for whatever reads them next.
  useEffect(() => {
    if (!ctx.active) return;
    const light = chrome.cfg.theme === "light";
    const vars: Record<string, string> = light
      ? { ln: "#39434D", lnd: "#6E7A85", blu: "#1668B8", yel: "#8A6410", red: "#BE3428",
          grn: "#2C7A31", mag: "#7E42A0", paper: "#FFFFFF", ink: "#59636D" }
      : { ln: "#CED5DA", lnd: "#8B959C", blu: "#5AA0DA", yel: "#E3B23C", red: "#D9584F",
          grn: "#5FAE63", mag: "#B57BD0", paper: "#3C4147", ink: "#20242A" };
    const root = document.documentElement;
    for (const [k, v] of Object.entries(vars)) root.style.setProperty(`--acad-${k}`, v);
    return () => {
      for (const k of Object.keys(vars)) root.style.removeProperty(`--acad-${k}`);
    };
  }, [ctx.active, chrome.cfg.theme]);

  // ── is a block open for editing? ─────────────────────────────────────────────────────────
  //
  // ⚠⚠ **ASK THE SERVER, DO NOT REMEMBER.** A BEDIT session is the exploded geometry itself, so
  // the drawing IS the session. Reload the page mid-edit and the shapes are still on the plan;
  // if this component trusted its own `useState` instead, they would be there with no way to
  // re-block them and no button admitting one was open. Runs once per plan, not per render.
  useEffect(() => {
    if (!ctx.active || !ctx.planId) return;
    let live = true;
    designBlockEdit(ctx.planId)
      .then((r: any) => {
        if (!live) return;
        setBedit(r && r.block ? { block: r.block, name: r.name || "", code: r.code || "" } : null);
      })
      .catch(() => { /* an unreachable API is the reload's problem to report, not this one's */ });
    return () => { live = false; };
  }, [ctx.active, ctx.planId]);

  // ⚠ Not a hook and not memoised: `ctx.panels` is filled AFTER this hook runs, so this closure
  // must read it at render time. Building the nodes eagerly would capture a null ref.
  //
  // ⚠ MAPPED ONTO THE PANELS THE STUDIO ALREADY HAS, not rebuilt. AutoCAD's Properties palette
  // IS our inspector, its Layer Properties Manager IS our layers panel, its Tool Palettes is
  // where our blocks live, and DWGHISTORY is our history. A second implementation of any of
  // them would drift within a week — another lane reached the same conclusion about its
  // Default Tray, independently.
  const paletteBody = (id: string): any => {
    const p = ctx.panels.current;
    if (!p) return null;
    const entry = PALETTES.find((x) => x.id === id);
    if (!entry?.panel) return null;
    return entry.panel === "@inspector" ? p.inspector() : p.panelBody(entry.panel);
  };

  // ── the status bar's readouts ───────────────────────────────────────────────────────
  // ⚠ AutoCAD prints X, Y and Z, and Z is genuinely 0 here: the plan canvas is one plane at the
  // model's origin. One decimal, not AutoCAD's four — these are millimetres across a 20 m villa,
  // and `12345.0000` is four digits of noise on the number people watch most.
  // ⚠⚠ **`cursorUcs`, NOT `cursor` — AutoCAD's box reads in the CURRENT UCS.** A drafter who
  // moved the origin onto a wall corner expects the box to read 0,0 there; a box that keeps
  // reading world coordinates makes the UCS look like it did nothing. ⚠ Falls back to `cursor`,
  // because a studio with no UCS concept at all publishes only that one.
  const readAt = (st as { cursorUcs?: { x: number; y: number } } | undefined)?.cursorUcs
                 ?? st?.cursor;
  const coords = readAt
    ? `${readAt.x.toFixed(1)}, ${readAt.y.toFixed(1)}, 0.0`
    : "—";
  const snapTag = st?.snap?.kind && st.snap.kind !== "free"
    ? (SNAP_TAG[st.snap.kind] || `_${st.snap.kind}`) : "";

  // ── what the command line is asking for ─────────────────────────────────────────────
  // ⚠ Built only out of things the canvas actually REPORTS. A prompt invented from the tool's
  // step list would go stale the moment a point was placed, and a command line that says
  // "Specify first point" while you are placing the third is worse than one that says nothing.
  const prompt = (() => {
    if (st?.pending) {
      const need = st.pending.needs === "point" ? 1 : 2;
      const done = (st.pending.pts || []).length;
      return `${st.pending.op.toUpperCase()} Specify point ${done + 1} of ${need}:`;
    }
    const a = T?.args || {};
    const cmd = active ? BTN_BY_ID[active]?.cmd : null;
    // ⚠ AutoCAD's prompt says what the command wants next, so an armed command's note goes here
    // rather than only in a tooltip nobody hovers mid-gesture. It matters most where this shell
    // has removed the canvas's own guidance: the dimension family is four commands wearing one
    // mode and its picker card is hidden here, so without this a drafter pressing DIMANGULAR
    // has nothing telling them the first click is the vertex.
    // ⚠ Suppressed once a number is being typed — the half-typed length is what the eye wants
    // then, and a sentence beside it is noise.
    if (active && cmd && mode !== "select" && !a.distance && !a.angle) {
      const note = (chrome.all[active] as any)?.note;
      if (note) return `${cmd}: ${note}`;
    }
    if (cmd && mode !== "select") {
      // ⚠ `<n>` is AutoCAD's own notation for the value a prompt will use if you just press
      // Enter, and POLYGON is the one command here whose answer lives outside the drawing.
      const dflt = mode === "polygon" ? `  <${T?.sides ?? 6}>` : "";
      const arg = a.angle ? `  <${a.angle}` : a.distance ? `  ${a.distance}` : "";
      return `${cmd}:${dflt}${arg}`;
    }
    return "Command:";
  })();

  // ── typing a command at the drawing ──────────────────────────────────────────────────────
  //
  // ⚠⚠⚠ **EVERY COMMAND HAD TO BE CLICKED, AND THE COMMENT ON `AcadCommandLine` SAID
  // OTHERWISE.** It claims that while the box is unfocused it "MIRRORS the studio's own command
  // buffer, so letters typed at the drawing still appear here". There is no such buffer:
  // `Studio.typed` is the DYNAMIC-INPUT value — the length or angle a running command is asking
  // for — and nothing anywhere collected a command NAME typed at the canvas. So `L ⏎` with the
  // mouse over the drawing did nothing whatsoever, and the only way to run anything was the
  // ribbon. Reported by the owner testing on the office PC, 2026-09-17; reproduced here.
  //
  // ⚠⚠ **AND THE OLD NOTE'S FEAR IS REAL, WHICH IS WHY THIS IS GUARDED RATHER THAN AUTOFOCUS.**
  // Autofocusing the box would break typed input across the whole studio: the canvas reads
  // lengths, angles and option keywords off its own key handler, which never sees a keystroke
  // while an `<input>` holds focus. `L` then `3000 ⏎` would put the 3000 in the command line.
  // ⇒ The keyboard is taken ONLY when nothing is running: `prompt` is the literal string
  // "Command:" and no tool is armed. During a command every keystroke goes where it already
  // went. Fail the guard and the behaviour is exactly what it was — clicking the box still
  // works, so this can only add.
  //
  // ⚠ LETTERS ONLY. A bare digit at the drawing is a typed distance in AutoCAD, not the start
  // of a command, and stealing it would break the one kind of typed input that DID work.
  // `'`-prefixed transparent commands and `-`-prefixed command-line versions are not taken
  // either; both are typed into the box, which is reachable by clicking as before.
  const promptRef = useRef(prompt);
  promptRef.current = prompt;
  const modeRef = useRef(mode);
  modeRef.current = mode;
  useEffect(() => {
    if (!ctx.active) return;
    const onType = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (e.key.length !== 1 || !/[A-Za-z]/.test(e.key)) return;
      const el = e.target as HTMLElement | null;
      const tag = el?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || el?.isContentEditable) return;
      // ⚠ Both, not either. `mode` alone misses a command that arms no drawing tool, and
      // `prompt` alone is built from things the CANVAS reports, so it can read "Command:"
      // while a mode is still armed.
      if (promptRef.current !== "Command:" || modeRef.current !== "select") return;
      const take = chrome.takeKeyboard.current;
      if (!take) return;
      e.preventDefault();
      take(e.key);
    };
    window.addEventListener("keydown", onType);
    return () => window.removeEventListener("keydown", onType);
  }, [ctx.active, chrome]);

  const drops: Record<string, AcadMenuItem[]> = {
    osnap: osnapMenu(),
    workspace: WORKSPACES,
    units: [{ id: "file.units", label: "Millimeters", radio: true }],
    annoscale: ["1:1", "1:2", "1:5", "1:10", "1:20", "1:50", "1:100"].map((k) => ({
      id: `annoscale.${k.replace(":", "_")}`, label: k, radio: true })),
    // ⚠ AutoCAD's polar increments. A drop-up that only LISTS them would be the same lie as a
    // grey button with no reason — picking one sets POLARANG and turns polar on, which is what
    // clicking an angle plainly means.
    polarang: POLAR_ANGLES.map((a) => ({ id: `polarang.${a}`, label: `${a}°`, radio: true })),
  };

  if (!ctx.active) return {};

  const title = ctx.plan?.name || "Drawing1";
  return {
    // ⚠ The host's way into the command-line transcript — see `StudioChrome.say`. It is the
    // same `say` every handler in this file uses, so the host's lines and the chrome's own
    // interleave in one history rather than living in two places.
    say: (line: string) => chrome.say(line),
    // ⚠ The host's Ctrl+P reaches the chrome's Plot dialog through here — see `StudioChrome`.
    openPlot: (what: "drawing" | "set") => setPlot(what),
    top: (<>
      <AcadTitleBar chrome={chrome} title={title} dirty={!!st?.dirty} ws={ws}
        user={ctx.user} onUser={() => ctx.nav("/signout")} />
      {chrome.cfg.menubar ? <AcadMenuBar chrome={chrome} /> : null}
      {chrome.cfg.clean ? null : ribbon}
      <AcadFileTabs chrome={chrome} title={title} dirty={!!st?.dirty} />
    </>),
    // ⚠ The UCS icon rides the RAIL, after any docked toolbars, so its zero-width strip is the
    // flex sibling immediately LEFT of the drawing — which is the only place a positive offset
    // lands it at the drawing's bottom-left corner, where AutoCAD draws it.
    rail: (<>
      {toolbars}
      <AcadUcsIcon chrome={chrome} ucs={st?.ucs} />
    </>),
    // ⚠ TWO nodes, and the first has zero width: the ViewCube, the navigation bar and the UCS
    // icon float OVER the drawing's right edge, which a flex sibling can do with a negative
    // offset and no ref to the canvas.
    side: (<>
      <AcadCanvasFurniture chrome={chrome} />
      {/* ⚠ BEFORE the palettes, so the order across the screen is drawing · second viewport ·
          palettes — which is where AutoCAD puts a docked palette relative to model space. */}
      <AcadViewportPane chrome={chrome} on={vports === "2v" && space === null}
        title="Viewport 2 — 3D">
        <Suspense fallback={
          <div data-testid="acad-vport-loading"
            style={{ height: "100%", display: "grid", placeItems: "center",
                     color: chrome.ui.soft, font: `${chrome.ui.size}px ${chrome.ui.font}` }}>
            Regenerating model…</div>}>
          {/* ⚠ A DIFFERENT `key` from the full-area one. Sharing it would make React reuse the
              instance across the two mount points and re-fetch the whole model on every split. */}
          <Model3D key={`vp-${ctx.planId}-${modelRev}`} planId={ctx.planId} bare
            onState={setM3d} />
        </Suspense>
      </AcadViewportPane>
      <AcadPalettes chrome={chrome} body={paletteBody} />
    </>),
    status: (<>
      <AcadLayoutTabs chrome={chrome} layouts={pages} />
      <AcadCommandLine chrome={chrome} typed={T?.typed} prompt={prompt} />
      <AcadStatusBar chrome={chrome} coords={coords} snap={snapTag} drops={drops} />
    </>),
    overlay: (<>
      <AcadThemeBridge chrome={chrome} />
      <AcadAppMenu chrome={chrome} />
      <AcadOptions chrome={chrome} />
      {/* ⚠ The pickers are read from the canvas at RENDER time, not captured into the handler
          memo: a drawing gains and loses shape kinds while you work, and a filter list frozen
          when the shell mounted would offer kinds that are gone and miss the ones you drew. */}
      <AcadQSelect chrome={chrome} open={qsel} close={() => setQSel(false)}
        kinds={A?.selectableKinds?.().kinds || []}
        wallKinds={A?.selectableKinds?.().wallKinds || []}
        hasBlocks={!!A?.selectableKinds?.().hasBlocks}
        layers={(ctx.plan?.layers || []).map((l: any) => ({ id: l.id, name: l.name }))}
        onGo={(f) => A?.selectBy?.(f)} />
      <AcadFind chrome={chrome} open={findOpen} close={() => setFindOpen(false)}
        onFind={(q) => A?.findText?.(q) ?? 0} />
      <AcadTestDrive open={testDrive} close={() => setTestDrive(false)}
        planId={ctx.planId} />
      <AcadViewManager chrome={chrome} open={viewMgr} close={() => setViewMgr(false)}
        scenes={m3d?.tool?.scenes || []} current={scene} cam3d={camera !== "plan"}
        onSet={(n) => { setScene(n); go3d((a) => a.scene(n)); }}
        onNew={(n) => { if (n) { setScene(n); go3d((a) => a.sceneSave(n)); } }}
        onDelete={(n) => { if (n) { void go3d((a) => a.sceneDelete(n));
                                    if (scene === n) setScene(""); } }} />
      <AcadPlot chrome={chrome} what={plot} cfg={plotCfg} set={setPlotCfg}
        close={() => setPlot(null)} onPlot={(pv) => doPlot(plot || "drawing", pv)}
        sheets={sheets} busy={plotting}
        onSheet={space === null
                 ? null : ((own || pages).find((p) => p.id === space)?.name ?? null)} />
      {/* ⚠ `layers` comes from `ctx.plan` at RENDER time for the same reason the QSELECT
          pickers do: a merge deletes a layer and an import creates six, and a list frozen into
          the handler memo would offer names that are gone. */}
      <AcadLayerTool chrome={chrome} dlg={layerDlg} close={() => setLayerDlg(null)}
        layers={ctx.plan?.layers || []}
        counts={(n) => A?.layerCount?.(n) ?? 0}
        onPatch={patchLayers}
        onMerge={(from, to) => {
          const ids = A?.layerShapeIds?.(from) || [];
          const moved = ids.length ? (A?.moveToLayer?.(to, ids) || 0) : 0;
          const row = (ctx.plan?.layers || []).find((l: any) => l.name === from);
          ctx.run("acad-layer", async () => {
            // ⚠ THE ORDER IS THE WHOLE COMMAND — see `afterSaved`.
            if (!await afterSaved()) {
              chromeRef.current?.say(`  ${from} NOT deleted — the move has not saved yet`);
              return;
            }
            if (row) await designLayersApi.remove(row.id);
            await ctx.reload();
            chromeRef.current?.say(`  ${moved} object${moved === 1 ? "" : "s"} → ${to}, `
                                   + `layer ${from} deleted`);
          });
        }}
        onErase={(n) => {
          const gone = A?.eraseLayer?.(n) || 0;
          const row = (ctx.plan?.layers || []).find((l: any) => l.name === n);
          ctx.run("acad-layer", async () => {
            if (!await afterSaved()) {
              chromeRef.current?.say(`  ${n} NOT deleted — the erase has not saved yet`);
              return;
            }
            if (row) await designLayersApi.remove(row.id);
            await ctx.reload();
            chromeRef.current?.say(`  ${n} deleted with ${gone} object${gone === 1 ? "" : "s"}`);
          });
        }} />
      <AcadParamsDialog chrome={chrome} open={paramsOpen} close={() => setParamsOpen(false)}
        load={() => designParametric(ctx.planId)}
        post={async (body) => {
          const r = await designParametricPost(ctx.planId, body);
          // ⚠ THE RELOAD IS THE POINT. A constraint WRITES the drawing's vertices — unlike the
          // booleans, which derive — so a canvas that is not re-read shows the geometry as it
          // was and the whole feature looks like it did nothing.
          await ctx.reload();
          return r;
        }}
        del={async (q) => {
          const r = await designParametricDel(ctx.planId, q);
          await ctx.reload();
          return r;
        }} />
      {/* ⚠ `st.selected` already reports the picked instance AND its definition — see the memo
          in `PlanStudio`. A second selection channel for this dialog would be a second answer to
          "what is picked", which is how a window ends up editing the wrong block. */}
      <AcadAttrDialog chrome={chrome} open={!!attrTab} tab={attrTab || "defs"}
        block={(() => {
          const sel = facade.current?.st?.selected;
          return sel?.kind === "block" && sel.def
            ? { id: sel.def.id, name: sel.def.name || "block" } : null;
        })()}
        instance={(() => {
          const sel = facade.current?.st?.selected;
          return sel?.kind === "block" && sel.obj
            ? { id: sel.obj.id, block: sel.obj.block,
                name: sel.def?.name || "block" } : null;
        })()}
        close={() => setAttrTab("")}
        load={(b) => designBlockAttrs(ctx.planId, b)}
        save={(b, defs) => designSetBlockAttrs(ctx.planId, { block: b, defs })}
        sync={(b) => designSetBlockAttrs(ctx.planId, { block: b, op: "sync" })}
        setValues={async (i, values) => {
          const r = await designSetBlockAttrs(ctx.planId, { instance: i, values });
          await ctx.reload();
          return r;
        }} />
      <AcadBodyDialog chrome={chrome} open={!!bodyKind} kind={bodyKind}
        selCount={(facade.current?.st?.selShapes || []).length}
        close={() => setBodyKind("")}
        onMake={async (kind, vals) => {
          // ⚠⚠ PLACED AT THE MIDDLE OF WHAT IS ON SCREEN, not at the origin. The drawing's
          // origin on a real survey is 28 m away — the same reason `PlanStudio` reports its
          // view rectangle for menu-placed references. A body created off-screen reads as a
          // command that did nothing.
          const v = facade.current?.T?.view;
          const at = v ? { x: v.x + v.w / 2, y: v.y + v.h / 2 } : { x: 0, y: 0 };
          const ids = (facade.current?.st?.selShapes || []).map((i: any) => Number(i));
          const body: any = { kind, x: at.x, y: at.y, z: 0, ...vals };
          if (vals.sweep !== undefined) { body.params = { sweep: vals.sweep }; delete body.sweep; }
          if (ids.length) body.profile = { m: "shape", ids };
          const r = await designAddBody(ctx.planId, body);
          // ⚠ The 3D view holds the payload it was mounted with, so a new body needs the same
          // remount the booleans use — `ctx.reload()` refreshes the DRAWING, not the model.
          setModelRev((n) => n + 1);
          chromeRef.current?.say(`  ${kind} #${r?.id} created`);
          return r;
        }} />
      <AcadPdfImport chrome={chrome} open={pdfOpen} close={() => setPdfOpen(false)}
        onProbe={(f, page) => designPdfProbe(ctx.planId, f, page)}
        onImport={async (f, opts) => {
          const r = await designPdfImport(ctx.planId, f, opts);
          // ⚠ THE RE-READ IS THE COMMAND, same as the DWG import below: the server writes the
          // shapes and layers straight into the plan, so a canvas that is not re-read shows the
          // drawing as it was before the import and looks like nothing happened.
          await ctx.reload();
          chromeRef.current?.say(`  ${r?.report?.created?.shapes || 0} shapes, `
            + `${r?.report?.created?.texts || 0} texts at 1:${r?.report?.scale}`);
          return r?.report || r;
        }} />
      <AcadImport chrome={chrome} open={importOpen} close={() => setImportOpen(false)}
        onImport={async (file, opts) => {
          const r = await designImportCad(ctx.planId, file, opts);
          // ⚠⚠ THE RE-READ IS THE COMMAND. The import writes shapes and layers straight into
          // Postgres; the canvas is drawing what the page fetched when it opened. Without this
          // the drafter sees an encouraging report over an unchanged drawing, which reads as
          // the import having silently failed — the exact fault `ShellContext.reload` exists
          // to prevent.
          await ctx.reload();
          const n = r?.report?.created?.shapes || 0;
          chromeRef.current?.say(`  ${file.name} · ${n} shapes, `
                                 + `${r?.report?.created?.layers || 0} layers`);
          return r;
        }} />
    </>),
    // ⚠ The whole canvas area, and only when the camera has left Top view. `key` on the plan id
    // alone: entering a 3D view must not refetch the model every time the visual style changes.
    // ⚠ The fallback is the shell's own grey, not a spinner over white — the chunk arrives in
    // well under a second on the LAN and a flash of white reads as the drawing disappearing.
    // ⚠ ONE arbiter, in one place. A layout wins over the camera: you cannot be looking at
    // paper and orbiting the model at once, and two slots fighting over the same area is how a
    // blank canvas happens. Model space + Top view is the only state that leaves this undefined,
    // which is what lets the studio draw its own canvas.
    viewport: space !== null ? (
      <Suspense fallback={
        <div data-testid="acad-sheet-loading"
          style={{ height: "100%", display: "grid", placeItems: "center",
                   background: chrome.ui.canvas3d, color: chrome.ui.soft,
                   font: `${chrome.ui.size}px ${chrome.ui.font}` }}>
          Regenerating layout…</div>}>
        <div style={{ height: "100%", overflow: "auto", background: chrome.ui.canvas3d }}>
          <SheetComposer pageId={space} />
        </div>
      </Suspense>
    ) : camera === "plan" ? undefined : (
      <Suspense fallback={
        <div data-testid="acad-3d-loading"
          style={{ height: "100%", display: "grid", placeItems: "center",
                   background: chrome.ui.canvas3d, color: chrome.ui.soft,
                   font: `${chrome.ui.size}px ${chrome.ui.font}` }}>
          Regenerating model…</div>}>
        <Model3D key={`${ctx.planId}-${modelRev}`} planId={ctx.planId} bare
          onState={setM3d} />
      </Suspense>),
    hide: { top: true, rail: true, panel: true, props: true, status: true },
    // ⚠ The canvas's right button, answered where `lastId` actually lives. See `shell-types.ts`.
    repeatLast: () => { if (!chrome.lastId) return false; chrome.fire(chrome.lastId); return true; },
    fire: (id: string) => chrome.fire(id),
    // ⚠ The LISP seam, both ways: the command line hands a `(` line to the host, and asks the
    // host about a name it does not know. See `shell-types.ts`.
    evalLisp: chrome.evalLisp,
    lispCmd: chrome.lispCmd,
    // ⚠ The tool-options strip stays — it carries wall thickness, draw layer and pen, and
    // AutoCAD has no wall tool so it has nowhere native to put a wall thickness. It wears the
    // ribbon's own grey instead, so it reads as one more docked bar rather than a piece of a
    // different application. ⚠ The antd controls INSIDE it are the studio's, not this shell's,
    // and will not follow the theme; that row is the one deliberate seam.
    optionsStyle: {
      background: chrome.ui.ribbon, borderBottom: `1px solid ${chrome.ui.line}`,
      color: chrome.ui.fg,
    },
    // ⚠ NOT set, deliberately. `showAxes` draws coloured drawing axes THROUGH the origin;
    // AutoCAD has a UCS icon in the corner instead, and this shell draws its own — pointing the
    // way this drawing's Y actually runs, which is down.
  };
}

export const acadShell: StudioShell = {
  id: "acad",
  useChrome: useAutoCadStudioChrome,
};

export default acadShell;
